import { useEffect, useRef, useState } from "react";
import { EventBus } from "../game/EventBus";
import { EVENTS_NAME } from "../game/consts";

/**
 * The 0a CIPHER victory sequence — mission 5000, the multibase-number haiku.
 *
 * Sixty seconds, deliberately over the top, because this is the hardest
 * authored puzzle in the game and the only one signed by a real person.
 *
 * Structure is one canvas running four particle systems whose intensity is a
 * function of elapsed time, plus DOM captions on top. Acts are described by an
 * envelope rather than a state machine so there is no way to get stuck in one:
 * if a frame is dropped, or the tab is backgrounded and RAF stalls, the next
 * frame simply renders whatever `t` says it should be.
 *
 * Purely presentational. The win was scored server-side before this mounts.
 */

const MISSION_ID = 5000;
const RUN_MS = 60_000;

// Base-36 is the joke: the puzzle is about numbers that refuse to stay in
// base ten, so the rain is literally every digit a multibase number can use.
const GLYPHS = "0123456789abcdefghijklmnopqrstuvwxyz";

const PALETTE = ["#1FA8E0", "#00e5c7", "#ffd75a", "#ff7ac2", "#c86bff", "#e8f4fa"];
const PETAL_COLORS = ["#ffd7e6", "#ffb3d1", "#ff8fc0", "#ffe3ef", "#f7a8c8"];

// Captions, keyed to the seconds they own. Read top to bottom they retell the
// puzzle: the base falls out, the signal resolves, the name arrives.
const ACTS: { at: number; big: string; small: string }[] = [
  { at: 0, big: "BASE TEN IS LAZY", small: "…AND YOU STOPPED NOTICING" },
  { at: 9, big: "5 · 7 · 5", small: "SEVENTEEN SYLLABLES, NINETEEN TIMES" },
  { at: 18, big: "RADIX RESOLVED", small: "EVERY POSITION SPEAKS A DIFFERENT LANGUAGE" },
  { at: 27, big: "THE RECORD PLAYS", small: "ONE ARTIST. NOT A GUESS." },
  { at: 36, big: "0a's CIPHER", small: "BROKEN" },
  { at: 46, big: "STAND PROUD", small: "YOU, AND YOUR FAMILY, FOR GENERATIONS" },
];

function currentAct(sec: number) {
  let act = ACTS[0];
  for (const a of ACTS) {
    if (sec >= a.at) act = a;
  }
  return act;
}

export function CipherVictory() {
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState("");
  const [sec, setSec] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // ── Trigger ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onWin = (payload: { mission_id: number; response: string }) => {
      if (payload?.mission_id !== MISSION_ID) return;
      setResponse(payload.response || "");
      setSec(0);
      setOpen(true);
    };
    EventBus.on(EVENTS_NAME.cipherVictory, onWin);
    return () => {
      EventBus.removeListener(EVENTS_NAME.cipherVictory, onWin);
    };
  }, []);

  // ── Clock ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      const elapsed = Date.now() - started;
      setSec(Math.floor(elapsed / 1000));
      if (elapsed >= RUN_MS) setOpen(false);
    }, 250);
    return () => window.clearInterval(id);
  }, [open]);

  // ── The show ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);

    // Column layout for the digit rain has to be rebuilt on resize, or the
    // rain either stops short of the edge or writes off-screen.
    const COL_W = 18;
    let drops: number[] = [];
    const layoutRain = () => {
      const cols = Math.ceil(w / COL_W);
      drops = new Array(cols).fill(0).map(() => Math.random() * -60);
    };
    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      layoutRain();
    };
    layoutRain();
    window.addEventListener("resize", onResize);

    interface Spark {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      color: string;
      size: number;
    }
    interface Petal {
      x: number;
      y: number;
      vy: number;
      drift: number;
      phase: number;
      spin: number;
      size: number;
      color: string;
    }
    interface Ring {
      x: number;
      y: number;
      r: number;
      color: string;
    }

    const sparks: Spark[] = [];
    const petals: Petal[] = [];
    const rings: Ring[] = [];
    const MAX_SPARKS = 1200;
    const MAX_PETALS = 220;

    const boom = (x: number, y: number, color: string) => {
      rings.push({ x, y, r: 2, color });
      const n = 55 + Math.floor(Math.random() * 45);
      for (let i = 0; i < n && sparks.length < MAX_SPARKS; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = 1.4 + Math.random() * 5.5;
        sparks.push({
          x,
          y,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s,
          life: 0.75 + Math.random() * 0.55,
          color,
          size: 1.5 + Math.random() * 2.5,
        });
      }
    };

    const addPetal = (fromTop: boolean) => {
      if (petals.length >= MAX_PETALS) return;
      petals.push({
        x: Math.random() * w,
        y: fromTop ? -20 : Math.random() * h,
        vy: 0.5 + Math.random() * 1.5,
        drift: 0.4 + Math.random() * 1.2,
        phase: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.06,
        size: 4 + Math.random() * 7,
        color: PETAL_COLORS[Math.floor(Math.random() * PETAL_COLORS.length)],
      });
    };

    ctx.fillStyle = "#04060e";
    ctx.fillRect(0, 0, w, h);

    const t0 = Date.now();
    let frame = 0;
    let raf = 0;

    const tick = () => {
      frame++;
      const el = (Date.now() - t0) / 1000; // seconds elapsed

      // Envelopes. Rain dominates the first third and hands over to petals
      // and fireworks; everything overlaps rather than cutting, so no single
      // dropped frame is ever a visible transition.
      const rainAmt = el < 20 ? 1 - el / 20 : 0;
      const petalAmt = Math.min(1, Math.max(0, (el - 14) / 8));
      const fireAmt = Math.min(1, Math.max(0, (el - 18) / 6)) * (el > 54 ? 0.35 : 1);

      // Phosphor trail. Heavier late so the closing plaque stays readable.
      ctx.fillStyle = el > 44 ? "rgba(4,6,14,0.34)" : "rgba(4,6,14,0.20)";
      ctx.fillRect(0, 0, w, h);

      // ── 1. multibase digit rain ──
      if (rainAmt > 0.01) {
        ctx.font = "16px 'Courier New', monospace";
        for (let i = 0; i < drops.length; i++) {
          if (Math.random() > 0.55 * rainAmt + 0.25) continue;
          const ch = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
          const y = drops[i] * 18;
          // Leading glyph is white-hot, the tail is cyan — the usual trick
          // for making a rain column read as motion in a still frame.
          ctx.fillStyle = Math.random() > 0.9 ? "#ffffff" : "#1FA8E0";
          ctx.globalAlpha = 0.16 + 0.75 * rainAmt;
          ctx.fillText(ch, i * COL_W, y);
          drops[i] = y > h && Math.random() > 0.975 ? 0 : drops[i] + 1;
        }
        ctx.globalAlpha = 1;
      }

      // ── 2. cherry blossom ── (the eclipse haiku page, brought in-game)
      if (petalAmt > 0.01) {
        if (frame % 3 === 0) {
          for (let i = 0; i < Math.ceil(petalAmt * 2); i++) addPetal(true);
        }
        for (let i = petals.length - 1; i >= 0; i--) {
          const p = petals[i];
          p.phase += p.spin + 0.02;
          p.x += Math.sin(p.phase) * p.drift;
          p.y += p.vy;
          if (p.y > h + 25) {
            petals.splice(i, 1);
            continue;
          }
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.phase);
          ctx.globalAlpha = 0.85 * petalAmt;
          ctx.fillStyle = p.color;
          // A petal, not a square: two arcs meeting at a point.
          ctx.beginPath();
          ctx.ellipse(0, 0, p.size, p.size * 0.55, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
        ctx.globalAlpha = 1;
      }

      // ── 3. fireworks ──
      if (fireAmt > 0.01 && frame % Math.max(6, Math.round(20 - 14 * fireAmt)) === 0) {
        boom(
          w * (0.08 + 0.84 * Math.random()),
          h * (0.08 + 0.55 * Math.random()),
          PALETTE[Math.floor(Math.random() * PALETTE.length)],
        );
      }
      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.045;
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.life -= 0.012;
        if (p.life <= 0) {
          sparks.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.min(1, Math.max(0, p.life));
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
      }
      ctx.globalAlpha = 1;

      // ── 4. shockwave rings ──
      for (let i = rings.length - 1; i >= 0; i--) {
        const r = rings[i];
        r.r += 7;
        if (r.r > Math.max(w, h)) {
          rings.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.max(0, 0.5 - r.r / Math.max(w, h));
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      raf = requestAnimationFrame(tick);
    };

    // Opening volley so the very first frame is already a party.
    for (let i = 0; i < 5; i++) {
      boom(w * (0.2 + 0.6 * Math.random()), h * (0.15 + 0.5 * Math.random()), PALETTE[i]);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  if (!open) return null;

  const act = currentAct(sec);
  const finale = sec >= 36;

  return (
    <div className="cv-overlay">
      <style>{CV_CSS}</style>
      <canvas ref={canvasRef} className="cv-canvas" />
      <div className="cv-scan" />
      <div className="cv-sigil-wrap" aria-hidden="true">
        <div className="cv-sigil">0a</div>
        <div className="cv-orbit cv-orbit-a" />
        <div className="cv-orbit cv-orbit-b" />
      </div>
      <div className="cv-center">
        <div className="cv-eyebrow">CIPHER BROKEN</div>
        <div key={act.big} className="cv-big" data-text={act.big}>
          {act.big}
        </div>
        <div key={act.small} className="cv-small">
          {act.small}
        </div>
        {finale && response && <div className="cv-blessing">{response}</div>}
        {finale && (
          <button className="cv-btn" onClick={() => setOpen(false)}>
            RETURN TO THE STREET
          </button>
        )}
      </div>
      {/* Always skippable. Sixty seconds is a long time to hold someone's
          screen hostage, and a player mid-fight needs a way out. */}
      <button className="cv-skip" onClick={() => setOpen(false)}>
        SKIP
      </button>
    </div>
  );
}

const CV_CSS = `
.cv-overlay{position:fixed;inset:0;z-index:3500;overflow:hidden;
  font-family:'MelGame','Courier New',monospace;background:#04060e;}
.cv-canvas{position:absolute;inset:0;}
.cv-scan{position:absolute;inset:0;pointer-events:none;opacity:0.16;
  background:repeating-linear-gradient(to bottom,rgba(255,255,255,0.10) 0 1px,
  transparent 1px 3px);animation:cv-roll 6s linear infinite;}
@keyframes cv-roll{to{background-position:0 120px;}}

.cv-sigil-wrap{position:absolute;inset:0;display:flex;align-items:center;
  justify-content:center;pointer-events:none;}
.cv-sigil{font-size:min(52vh,52vw);font-weight:700;line-height:1;
  color:rgba(232,244,250,0.09);letter-spacing:-0.04em;
  text-shadow:0 0 60px rgba(31,168,224,0.45);
  animation:cv-breathe 7s ease-in-out infinite;}
@keyframes cv-breathe{0%,100%{transform:scale(1) rotate(-2deg);opacity:0.55;}
  50%{transform:scale(1.09) rotate(2deg);opacity:1;}}
.cv-orbit{position:absolute;border-radius:50%;
  border:1px solid rgba(31,168,224,0.22);}
.cv-orbit-a{width:min(70vh,70vw);height:min(70vh,70vw);
  border-style:dashed;animation:cv-spin 22s linear infinite;}
.cv-orbit-b{width:min(88vh,88vw);height:min(88vh,88vw);
  border-color:rgba(255,215,90,0.18);
  animation:cv-spin 34s linear infinite reverse;}
@keyframes cv-spin{to{transform:rotate(360deg);}}

.cv-center{position:relative;z-index:2;height:100%;display:flex;
  flex-direction:column;align-items:center;justify-content:center;
  gap:14px;padding:24px;text-align:center;}
.cv-eyebrow{font-size:13px;letter-spacing:0.55em;color:#ffd75a;opacity:0.8;
  text-shadow:0 0 14px rgba(255,215,90,0.7);}
.cv-big{position:relative;font-size:clamp(32px,7.5vw,86px);font-weight:700;
  letter-spacing:0.1em;color:#e8f4fa;
  text-shadow:0 0 26px rgba(31,168,224,0.95),0 0 70px rgba(255,122,194,0.45);
  animation:cv-slam 0.55s cubic-bezier(.2,1.6,.4,1);}
@keyframes cv-slam{0%{transform:scale(2.2);opacity:0;filter:blur(14px);}
  60%{opacity:1;}100%{transform:scale(1);opacity:1;filter:blur(0);}}
.cv-big::before,.cv-big::after{content:attr(data-text);position:absolute;
  left:0;top:0;width:100%;overflow:hidden;pointer-events:none;}
.cv-big::before{color:#ff7ac2;transform:translate(-3px,0);opacity:0.6;
  clip-path:inset(10% 0 60% 0);animation:cv-slice 2.7s infinite steps(1);}
.cv-big::after{color:#00e5c7;transform:translate(3px,0);opacity:0.6;
  clip-path:inset(64% 0 6% 0);animation:cv-slice 2.1s infinite steps(1) reverse;}
@keyframes cv-slice{0%{clip-path:inset(10% 0 60% 0);}25%{clip-path:inset(46% 0 24% 0);}
  50%{clip-path:inset(76% 0 6% 0);}75%{clip-path:inset(26% 0 46% 0);}
  100%{clip-path:inset(10% 0 60% 0);}}
.cv-small{font-size:clamp(12px,2vw,19px);letter-spacing:0.32em;color:#00e5c7;
  text-shadow:0 0 14px rgba(0,229,199,0.8);animation:cv-fade 0.9s ease;}
@keyframes cv-fade{from{opacity:0;transform:translateY(10px);}to{opacity:1;}}

.cv-blessing{margin-top:10px;max-width:760px;white-space:pre-wrap;
  font-size:clamp(15px,2.2vw,21px);line-height:1.6;color:#ffe9a8;
  background:rgba(24,16,2,0.68);border:1px solid rgba(255,215,90,0.55);
  border-radius:8px;padding:16px 22px;
  animation:cv-fade 1.2s ease, cv-glow 2.4s ease-in-out infinite;}
@keyframes cv-glow{0%,100%{box-shadow:0 0 16px rgba(255,215,90,0.35);}
  50%{box-shadow:0 0 46px rgba(255,215,90,0.85);}}

.cv-btn{margin-top:6px;background:#0a1c26;border:1px solid #1FA8E0;
  color:#1FA8E0;padding:12px 26px;border-radius:6px;cursor:pointer;
  font-family:inherit;font-size:18px;letter-spacing:0.14em;}
.cv-btn:hover{background:#103040;box-shadow:0 0 18px rgba(31,168,224,0.6);}
.cv-skip{position:absolute;right:14px;bottom:14px;z-index:3;
  background:transparent;border:1px solid rgba(232,244,250,0.35);
  color:rgba(232,244,250,0.6);padding:6px 14px;border-radius:4px;
  cursor:pointer;font-family:inherit;font-size:12px;letter-spacing:0.25em;}
.cv-skip:hover{color:#e8f4fa;border-color:#e8f4fa;}

@media (prefers-reduced-motion: reduce){
  .cv-sigil,.cv-orbit,.cv-big,.cv-small,.cv-blessing,.cv-scan{animation:none;}
}
`;

export default CipherVictory;
